import { BookabilityStatus, type OpenShift } from "@clipboard-health/contract-worker-app-bff";

import type { ShiftDouble } from "./types";

/**
 * Groups shifts into doubles by finding adjacent shift pairs at the same facility
 * with matching qualification. Two shifts are adjacent when one's `end` exactly
 * equals the other's `start` (ISO string comparison).
 *
 * Returns a map of shiftId -> ShiftDouble. Each shift's entry contains ALL
 * adjacent companions (not just the first match). For a chain A→B→C, shift B
 * will have companionShifts: [A, C].
 *
 * Only BOOKABLE shifts are considered.
 *
 * Complexity: O(n) total.
 */
export function groupShiftsIntoDoubles(shifts: OpenShift[]): Map<string, ShiftDouble> {
  const doublesByShiftId = new Map<string, ShiftDouble>();

  // Group shifts by (workplaceId, qualification)
  const groups = new Map<string, OpenShift[]>();

  for (const shift of shifts) {
    if (shift.attributes.bookability?.status !== BookabilityStatus.BOOKABLE) {
      continue;
    }

    const workplaceId = shift.relationships.workplace.data.id;
    const { qualification } = shift.attributes;
    const key = `${workplaceId}-${qualification}`;

    const existing = groups.get(key);
    if (existing) {
      existing.push(shift);
    } else {
      groups.set(key, [shift]);
    }
  }

  // Within each group, find adjacent pairs using a startTime index
  for (const groupShifts of groups.values()) {
    // Build index: startTime -> shift
    const startTimeIndex = new Map<string, OpenShift[]>();

    for (const shift of groupShifts) {
      const { start } = shift.attributes;
      const existing = startTimeIndex.get(start);
      if (existing) {
        existing.push(shift);
      } else {
        startTimeIndex.set(start, [shift]);
      }
    }

    // For each shift, check if any shift starts when this one ends (successor)
    for (const shift of groupShifts) {
      const workplaceId = shift.relationships.workplace.data.id;
      const { qualification, end } = shift.attributes;

      const successors = startTimeIndex.get(end);
      if (!successors) {
        continue;
      }

      for (const successor of successors) {
        if (successor.id === shift.id) {
          continue;
        }

        // Link shift -> successor (append to companionShifts)
        const existingShiftDouble = doublesByShiftId.get(shift.id);
        if (existingShiftDouble) {
          if (!existingShiftDouble.companionShifts.some((c) => c.id === successor.id)) {
            existingShiftDouble.companionShifts.push(successor);
          }
        } else {
          doublesByShiftId.set(shift.id, {
            workplaceId,
            qualification,
            companionShifts: [successor],
          });
        }

        // Link successor -> shift (append to companionShifts)
        const existingSuccessorDouble = doublesByShiftId.get(successor.id);
        if (existingSuccessorDouble) {
          if (!existingSuccessorDouble.companionShifts.some((c) => c.id === shift.id)) {
            existingSuccessorDouble.companionShifts.push(shift);
          }
        } else {
          doublesByShiftId.set(successor.id, {
            workplaceId,
            qualification,
            companionShifts: [shift],
          });
        }
      }
    }
  }

  return doublesByShiftId;
}
